import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Video, Square, RotateCcw, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 20;
const MAX_BYTES = 7_000_000; // ≈ 5MB raw × 1.33 base64 overhead

export default function VideoRecorder({ onSend, onCancel, sending }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const liveRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoUrl) URL.revokeObjectURL(videoUrl);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveRef.current) {
        liveRef.current.srcObject = stream;
        liveRef.current.muted = true;
        await liveRef.current.play().catch(() => {});
      }
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
        ? "video/webm;codecs=vp8,opus"
        : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "";
      const mr = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        videoBitsPerSecond: 900_000,
        audioBitsPerSecond: 64_000,
      });
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        if (liveRef.current) liveRef.current.srcObject = null;
        if (blob.size > MAX_BYTES) {
          toast.error("Video too big — try a shorter clip (max ~5 MB).");
          setVideoBlob(null);
          setVideoUrl(null);
          setPreviewing(false);
          setSeconds(0);
          return;
        }
        setVideoBlob(blob);
        setVideoUrl(URL.createObjectURL(blob));
        setPreviewing(true);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setPreviewing(false);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stop();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Camera/mic blocked. Allow access and retry.");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const reset = () => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    setPreviewing(false);
    setSeconds(0);
  };

  const send = () => {
    if (!videoBlob) return;
    const reader = new FileReader();
    reader.onload = () => onSend(reader.result);
    reader.readAsDataURL(videoBlob);
  };

  const pct = Math.min(100, (seconds / MAX_SECONDS) * 100);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <label className="block text-xs font-bold text-[#C9C7D9] ml-1 uppercase tracking-[0.2em]">
        Video answer{" "}
        <span className="text-white/40 normal-case tracking-normal">· max {MAX_SECONDS}s · premium</span>
      </label>

      <div className="card-glow rounded-2xl p-5 flex flex-col items-center gap-4">
        <div className="relative w-full aspect-square max-w-xs rounded-2xl overflow-hidden bg-black/60 border border-white/10">
          {!previewing ? (
            <video
              ref={liveRef}
              className="w-full h-full object-cover"
              playsInline
              muted
              data-testid="video-live-preview"
            />
          ) : (
            <video
              ref={previewRef}
              src={videoUrl}
              className="w-full h-full object-cover"
              controls
              playsInline
              data-testid="video-preview-player"
            />
          )}
          {recording && (
            <>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-black/70 px-2.5 py-1 rounded-full">
                <span className="w-2 h-2 rounded-full bg-[#FF4D8D] animate-pulse" style={{ boxShadow: "0 0 10px #FF4D8D" }} />
                <span className="text-xs font-bold text-white tabular-nums">REC {String(seconds).padStart(2, "0")}s</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div
                  className="h-full bg-gradient-to-r from-[#FF4D8D] to-[#7B61FF] transition-[width] duration-[1000ms] ease-linear"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </>
          )}
          {!recording && !videoBlob && (
            <div className="absolute inset-0 flex items-center justify-center text-[#C9C7D9] text-sm font-semibold">
              Tap record to start
            </div>
          )}
        </div>

        {!videoBlob && !recording && (
          <button
            onClick={start}
            type="button"
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center glow-pink active:scale-95 hover:scale-105 transition"
            data-testid="video-record-start"
            aria-label="Start recording"
          >
            <Video className="w-7 h-7 text-white" />
          </button>
        )}
        {recording && (
          <button
            onClick={stop}
            type="button"
            className="w-16 h-16 rounded-full bg-[#FF4D8D] flex items-center justify-center active:scale-95 transition"
            style={{ boxShadow: "0 0 30px rgba(255,77,141,0.8), 0 0 60px rgba(255,77,141,0.4)" }}
            data-testid="video-record-stop"
            aria-label="Stop recording"
          >
            <Square className="w-6 h-6 text-white" fill="white" />
          </button>
        )}
        {videoBlob && !recording && (
          <div className="grid grid-cols-2 gap-3 w-full">
            <button
              onClick={reset}
              type="button"
              disabled={sending}
              className="bg-white/5 border border-white/10 text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-white/10 active:scale-95 disabled:opacity-50 transition"
              data-testid="video-redo-button"
            >
              <RotateCcw className="w-4 h-4" /> Redo
            </button>
            <button
              onClick={send}
              type="button"
              disabled={sending}
              className="btn-gradient rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95"
              data-testid="video-send-button"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onCancel}
        disabled={sending || recording}
        className="w-full bg-white/5 border border-white/10 text-white rounded-full py-3 font-bold hover:bg-white/10 disabled:opacity-50 transition"
        data-testid="video-cancel-button"
      >
        Cancel
      </button>
    </motion.div>
  );
}
